/**
 *  ShopDetail script file
**/
import apiService from '@/api/apiService'
import { mapMutations } from 'vuex'
import { MODULE_NAME, MUTATIONS } from '@/store/map'
import layerService, { tooltipStyle } from '@/api/layerService'
import queries from '@/api/queries'
import layerids from '@/api/layerids'
import { IconLayer } from '@deck.gl/layers'
import { analyses } from '@/api/widgets'

const TAB_NAMES = {
  province: 'province',
  postalcodes: 'postalcodes',
  grids: 'grids'
}

const postalcodeAll = () => ({ id: 'all', name: 'Todos los códigos postales' })
const gridAll = () => ({ id: 'all', name: 'Todos los grids' })

const POLYGON_COLOR = [0, 0, 0, 0]
const POLYGON_HOVER_COLOR = [22, 150, 157, 255 * 0.1]
const POLYGON_SELECTED_COLOR = [22, 150, 157, 255 * 0.2]

const POLYGON_LINE_WIDTH = 1
const POLYGON_LINE_WIDTH_SELECTED = 2

function getPolygonColor (id, hover, selection) {
  const hovered = id === hover
  const selected = selection.some(s => s.id === id)
  const allSelected = selection.length === 1 && selection[0].id === 'all'

  if (selected || allSelected) {
    return POLYGON_SELECTED_COLOR
  }
  if (hovered) {
    return POLYGON_HOVER_COLOR
  }
  return POLYGON_COLOR
}

function getPolygonLineWidth (id, hover, selection) {
  const hovered = id === hover
  const selected = selection.some(s => s.id === id)
  const allSelected = selection.length === 1 && selection[0].id === 'all'
  if (hovered || selected || allSelected) {
    return POLYGON_LINE_WIDTH_SELECTED
  }
  return POLYGON_LINE_WIDTH
}

const shopIconUrl = require('@/assets/icons/map/icon-store.svg')

export default {
  name: 'ShopDetail',
  mixins: [],
  components: {
    TagListSelector: () => import(/* webpackChunkName: "TagListSelector" */'@/components/tag-list-selector/TagListSelector.vue')
  },
  props: {},
  data () {
    return {
      center: {
        postalcodes_province: [],
        postalcodes: [],
        grids: []
      },
      loading: true,
      titleScrollReached: false,
      activeTab: TAB_NAMES.grids,
      provinceModeActive: false,
      selectedPostalcodes: [postalcodeAll()],
      selectedGrids: [gridAll()],
      hoveredPostalcode: null,
      hoveredGrid: null,
      ...layerids,
      boxSelectionEnabled: false,
      dragStart: null,
      dragCurrent: null
    }
  },
  computed: {
    centerid () {
      return this.$route.params.id
    },
    postalcodeProvinceData () {
      return this.center.postalcodes_province.map(code => ({ id: code, name: code }))
    },
    postalcodeOptions () {
      const opts = this.center.postalcodes.map(code => ({ id: code, name: code }))
      return [postalcodeAll()].concat(opts)
    },
    gridOptions () {
      const opts = this.center.grids.map(code => ({ id: code, name: code }))
      return [gridAll()].concat(opts)
    },
    areas () {
      if ([TAB_NAMES.postalcodes, TAB_NAMES.province].includes(this.activeTab)) {
        return this.provinceModeActive
          ? this.center.postalcodes_province
          : this.selectedPostalcodes.map(d => d.id)
      }
      if (this.activeTab === TAB_NAMES.grids) {
        return this.selectedGrids.map(d => d.id)
      }
    },
    activeLayer () {
      if (this.activeTab === TAB_NAMES.postalcodes) {
        return this.postalcodesLayerId
      }
      if (this.activeTab === TAB_NAMES.province) {
        return this.provincesLayerId
      }
      if (this.activeTab === TAB_NAMES.grids) {
        return this.gridsLayerId
      }
      return ''
    },
    analysisOpen () {
      return this.$route.name === 'shop-analysis'
    },
    analysisKey () {
      return this.$route.params.type
    },
    analysis () {
      return analyses.find(a => a.key === this.analysisKey)
    },
    // TODO: consider moving into a mixin everything related with boxSelection
    hasBoxSelection () {
      return this.dragStart && this.dragCurrent
    },
    boxSelectionStyles () {
      if (!this.hasBoxSelection) {
        return { display: 'none' }
      }
      const minX = Math.min(this.dragStart.x, this.dragCurrent.x)
      const minY = Math.min(this.dragStart.y, this.dragCurrent.y)
      const maxX = Math.max(this.dragStart.x, this.dragCurrent.x)
      const maxY = Math.max(this.dragStart.y, this.dragCurrent.y)
      return {
        display: 'block',
        transform: `translate(${minX}px, ${minY}px)`,
        width: `${maxX - minX - 12}px`,
        height: `${maxY - minY - 12}px`
      }
    }
  },
  watch: {
    centerid: {
      immediate: true,
      handler () {
        this.fetchCenter()
      }
    },
    hoveredGrid () {
      this.updateGridLayer()
    },
    selectedGrids () {
      this.updateGridLayer()
    },
    hoveredPostalcode () {
      this.updatePostalcodeLayer()
    },
    selectedPostalcodes () {
      this.updatePostalcodeLayer()
    },
    activeTab () {
      this.updateGridLayer()
      this.updatePostalcodeLayer()
      this.updateProvincesLayer()
    },
    provinceModeActive () {
      this.updatePostalcodeLayer()
    }
  },
  mounted () {
    this.$refs.scrollContent.addEventListener('scroll', this.onScroll, false)
  },
  beforeDestroy () {
    layerService.removeLayer(this.postalcodesLayerId)
    layerService.removeLayer(this.provincesLayerId)
    layerService.removeLayer(this.gridsLayerId)
    layerService.removeLayer(this.markerLayerId)
    this[MUTATIONS.RESET_VIEWSTATE]()
  },
  methods: {
    ...mapMutations(MODULE_NAME, [MUTATIONS.FLY_TO, MUTATIONS.RESET_VIEWSTATE]),
    async fetchCenter () {
      this.loading = true
      try {
        const data = await apiService.getShopDetail(this.centerid)
        this.center = data
        this.centerMap()
        await this.createMapLayers()
      } catch (err) {
        // eslint-disable-next-line
        console.error('[shop-detail.js] Error fetching center', err)
      }
      this.loading = false
    },
    centerMap (zoom = 11) {
      this[MUTATIONS.FLY_TO]({
        longitude: this.center.longitud,
        latitude: this.center.latitud,
        zoom
      })
    },
    async createMapLayers () {
      await this.createPostalcodeLayer()
      await this.createGridLayer()
      await this.createProvinceLayer()
      this.createShopMarker()
    },
    createShopMarker () {
      layerService.addLayer({
        id: this.markerLayerId,
        layerType: IconLayer,
        zIndex: 3,
        data: [[this.center.longitud, this.center.latitud]],
        getPosition: d => d,
        getIcon: () => ({
          width: 32,
          height: 32,
          url: shopIconUrl
        }),
        getSize: 48
      })
    },
    createPostalcodeLayer () {
      return layerService.addSQLLayer({
        id: this.postalcodesLayerId,
        sql: queries.postalcodes(this.centerid),
        visible: this.activeTab === TAB_NAMES.postalcodes,
        pickable: true,
        zIndex: 1,
        lineWidthUnits: 'pixels',
        getLineColor: [22, 150, 157],
        getFillColor: (object) => {
          const id = object.properties.code
          return getPolygonColor(id, this.hoveredPostalcode, this.selectedPostalcodes)
        },
        getLineWidth: (object) => {
          const id = object.properties.code
          return getPolygonLineWidth(id, this.hoveredPostalcode, this.selectedPostalcodes)
        },
        updateTriggers: {
          getFillColor: [this.selectedPostalcodes, this.hoveredPostalcode],
          getLineWidth: [this.selectedPostalcodes, this.hoveredPostalcode]
        },
        onHover: ({ object }) => {
          const id = object && object.properties.code
          this.hoveredPostalcode = id || null
        },
        onClick: ({ object }) => {
          if (!object) {
            return
          }
          const code = object.properties.code
          this.onPostalcodeSelected(code)
        },
        getTooltip ({ object }) {
          const text = `CP ${object.properties.code}`
          return { text, style: tooltipStyle }
        }
      })
    },
    updatePostalcodeLayer () {
      layerService.updateLayer(this.postalcodesLayerId, {
        visible: this.activeTab === TAB_NAMES.postalcodes && !this.provinceModeActive,
        updateTriggers: {
          getFillColor: [this.selectedPostalcodes, this.hoveredPostalcode],
          getLineWidth: [this.selectedPostalcodes, this.hoveredPostalcode]
        }
      })
    },
    createGridLayer () {
      return layerService.addSQLLayer({
        id: this.gridsLayerId,
        sql: queries.grids(this.centerid),
        visible: this.activeTab === TAB_NAMES.grids,
        pickable: true,
        zIndex: 1,
        lineWidthUnits: 'pixels',
        getLineColor: [22, 150, 157],
        getFillColor: (object) => {
          const id = object.properties.code
          return getPolygonColor(id, this.hoveredGrid, this.selectedGrids)
        },
        getLineWidth: (object) => {
          const id = object.properties.code
          return getPolygonLineWidth(id, this.hoveredGrid, this.selectedGrids)
        },
        updateTriggers: {
          getFillColor: [this.selectedGrids, this.hoveredGrid],
          getLineWidth: [this.selectedGrids, this.hoveredGrid]
        },
        onHover: ({ object }) => {
          const id = object && object.properties.code
          this.hoveredGrid = id || null
        },
        onClick: ({ object }) => {
          if (!object) {
            return
          }
          const code = object.properties.code
          this.onGridSelected(code)
        },
        getTooltip ({ object }) {
          const text = `Grid ${object.properties.code}`
          return { text, style: tooltipStyle }
        }
      })
    },
    createProvinceLayer () {
      return layerService.addSQLLayer({
        id: this.provincesLayerId,
        sql: queries.provinces(this.centerid),
        visible: this.activeTab === TAB_NAMES.province,
        pickable: true,
        zIndex: 1,
        lineWidthUnits: 'pixels',
        getLineColor: [22, 150, 157],
        getFillColor: POLYGON_SELECTED_COLOR,
        getLineWidth: POLYGON_LINE_WIDTH_SELECTED,
        getTooltip ({ object }) {
          const text = `${object.properties.nameunit}`
          return { text, style: tooltipStyle }
        }
      })
    },
    updateGridLayer () {
      layerService.updateLayer(this.gridsLayerId, {
        visible: this.activeTab === TAB_NAMES.grids,
        updateTriggers: {
          getFillColor: [this.selectedGrids, this.hoveredGrid],
          getLineWidth: [this.selectedGrids, this.hoveredGrid]
        }
      })
    },
    updateProvincesLayer () {
      layerService.updateLayer(this.provincesLayerId, {
        visible: this.activeTab === TAB_NAMES.province
      })
    },
    onPostalcodeSelected (code) {
      const selected = this.selectedPostalcodes.some(s => s.id === code)
      if (selected) {
        this.selectedPostalcodes = this.selectedPostalcodes.filter(s => s.id !== code)
      } else {
        if (code === 'all') {
          this.selectedPostalcodes = [postalcodeAll()]
        } else {
          this.selectedPostalcodes = this.selectedPostalcodes.concat({
            id: code,
            name: code
          }).filter(d => d.id !== 'all')
        }
      }
    },
    onGridSelected (code) {
      const selected = this.selectedGrids.some(s => s.id === code)
      if (selected) {
        this.selectedGrids = this.selectedGrids.filter(s => s.id !== code)
      } else {
        if (code === 'all') {
          this.selectedGrids = [gridAll()]
        } else {
          this.selectedGrids = this.selectedGrids.concat({
            id: code,
            name: code
          }).filter(d => d.id !== 'all')
        }
      }
    },
    onScroll () {
      const scrollTop = this.$refs.scrollContent.scrollTop
      this.titleScrollReached = scrollTop >= 54
    },
    onChangeTab ({ id }) {
      this.activeTab = id
      this.provinceModeActive = id === TAB_NAMES.province
      let zoom = 11
      switch (this.activeTab) {
        case TAB_NAMES.grid:
          zoom = 11
          break
        case TAB_NAMES.postalcodes:
          zoom = 10
          break
        case TAB_NAMES.province:
          zoom = 7
          break
      }
      this.centerMap(zoom)
      this.updateScrollbar()
    },
    updateScrollbar () {
      this.$refs.scrollbar.update()
    },
    toggleBoxSelection ({ state }) {
      if (state) {
        this.enableBoxSelection()
      } else {
        this.disableBoxSelection()
      }
    },
    enableBoxSelection () {
      this.boxSelectionEnabled = true
      layerService.deckInstance.setProps({
        controller: false,
        onDragStart: ({ x, y }) => {
          this.dragStart = { x, y }
        },
        onDrag: ({ x, y }) => {
          this.dragCurrent = { x, y }
        },
        onDragEnd: ({ x, y }) => {
          if (!this.hasBoxSelection) {
            return
          }
          const minX = Math.min(this.dragStart.x, x)
          const minY = Math.min(this.dragStart.y, y)
          const maxX = Math.max(this.dragStart.x, x)
          const maxY = Math.max(this.dragStart.y, y)

          this.updateSelectionFromBBOX([minX, minY, maxX, maxY])
          this.dragStart = null
          this.dragCurrent = null
        }
      })
    },
    disableBoxSelection () {
      this.boxSelectionEnabled = false
      layerService.deckInstance.setProps({
        controller: true,
        onDragStart: null,
        onDrag: null,
        onDragEnd: null
      })
    },
    updateSelectionFromBBOX (bbox) {
      const [minX, minY, maxX, maxY] = bbox
      const selection = layerService.deckInstance.pickObjects({
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        layerIds: [this.activeLayer]
      }).map(info => ({
        id: info.object.properties.code,
        name: info.object.properties.code
      }))

      if (this.activeTab === TAB_NAMES.postalcodes) {
        const selected = selection.filter(s => !this.selectedPostalcodes.some(p => p.id === s.id))
        this.selectedPostalcodes = this.selectedPostalcodes.concat(selected).filter(d => d.id !== 'all')
      }
      if (this.activeTab === TAB_NAMES.grids) {
        const selected = selection.filter(s => !this.selectedGrids.some(g => g.id === s.id))
        this.selectedGrids = this.selectedGrids.concat(selected).filter(d => d.id !== 'all')
      }
    }
  }
}
